/**
 * The account picker.
 *
 * Hand-built rather than MUI Autocomplete, for three reasons:
 *
 *  1. MUI's Autocomplete owns the Enter key -- it means "select the
 *     highlighted option" and stops there. Tally means "commit this field
 *     and jump to the next one". Fighting that on every cell produces a
 *     form that feels subtly wrong.
 *  2. A MUI TextField renders ~5 nested elements. A 20-line voucher is
 *     100 fields; that is 500 DOM nodes instead of 100, and it stutters
 *     while typing on an office PC.
 *  3. MUI renders its dropdown in a portal, which breaks tab order and
 *     focus restoration unless carefully managed.
 *
 * It is styled from the same theme tokens as everything else, so the user
 * never sees a seam.
 */
import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo,
  useRef, useState, type KeyboardEvent,
} from 'react'
import { Box, Paper, Popper, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLedgers } from '../store/LedgerCache'
import type { Ledger } from '../api/types'
import { c, tokens } from '../theme'
import { withSide } from './money'

export interface LedgerPickerHandle {
  focus: () => void
  select: () => void
}

interface Props {
  value: string                       // ledger code
  onChange: (code: string, ledger?: Ledger) => void
  /** Restrict to these account group codes. Empty means all. */
  groups?: string[]
  /** Restrict to these ledger codes (user scoping). Empty means all. */
  allowCodes?: string[]
  placeholder?: string
  disabled?: boolean
  readOnlyLabel?: string
  /** Enter or Tab with a chosen account: move on. */
  onCommit?: () => void
  onCreateRequest?: (typed: string) => void
  autoFocus?: boolean
  /**
   * Today's signed balance of an account (Dr +, Cr -), or undefined when
   * unknown to this user. Shown beside each option so the clerk can check
   * a customer's or supplier's position before posting.
   */
  balanceOf?: (code: string) => string | undefined
}

const MAX_RESULTS = 8

const LedgerPicker = forwardRef<LedgerPickerHandle, Props>(function LedgerPicker(
  { value, onChange, groups, allowCodes, placeholder, disabled,
    readOnlyLabel, onCommit, onCreateRequest, autoFocus, balanceOf },
  ref,
) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { search, byCode, ledgers } = useLedgers()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [editing, setEditing] = useState(false)

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
  }), [])

  const selected = value ? byCode(value) : undefined

  const restrict = useCallback((list: Ledger[]) => {
    let out = list
    // The LINEAGE of stable keys, not the display code and not the
    // ledger's own key.
    //
    // Two lessons, both learned the same way -- every restricted picker
    // silently coming up empty:
    //
    //   - the display code changes with the company's numbering style
    //     ('DEBTORS' in mnemonic, '1000.01.03' in dot-notation), so
    //     matching on it broke the moment a company switched;
    //   - the ledger's own key is only 'DEBTORS' while the chart is one
    //     level deep. File a customer under 'Jeddah region' and its key
    //     is 'JEDDAH_REGION'.
    //
    // group_keys holds the whole path, so both cases are one test.
    // Falling back to group_key keeps an older cached response working.
    if (groups?.length) {
      out = out.filter((l) => (l.group_keys ?? [l.group_key])
        .some((k) => groups.includes(k)))
    }
    if (allowCodes?.length) out = out.filter((l) => allowCodes.includes(l.code))
    return out
  }, [groups, allowCodes])

  const results = useMemo(() => {
    if (!editing) return []
    if (!query.trim()) {
      // Empty query with a restricted set: show the whole set. A cashier
      // with one till should see it without typing anything.
      const base = restrict(ledgers)
      return base.length <= MAX_RESULTS ? base : []
    }
    return restrict(search(query, MAX_RESULTS * 4)).slice(0, MAX_RESULTS)
  }, [editing, query, search, restrict, ledgers])

  useEffect(() => { setHighlight(0) }, [query])

  function choose(l: Ledger) {
    onChange(l.code, l)
    setQuery('')
    setEditing(false)
    setOpen(false)
    onCommit?.()
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setHighlight((h) => Math.min(h + 1, Math.max(results.length - 1, 0)))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
      return
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (open && results[highlight]) {
        e.preventDefault()
        choose(results[highlight])
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        // Nothing highlighted but an account is already set: just move on.
        if (selected) { setEditing(false); onCommit?.() }
      }
      return
    }
    if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); setEditing(false); setQuery('') }
      return
    }
    // Alt+C: create a party without losing the entry (Tally's Alt+C).
    if (e.altKey && (e.key === 'c' || e.code === 'KeyC')) {
      e.preventDefault()
      if (onCreateRequest) {
        // This box's own Alt+C wins over the screen's (on trip entry the
        // screen's Alt+C is "new customer"; in the owner box it means a
        // new subcontractor).
        e.stopPropagation()
        onCreateRequest(query)
      }
    }
  }

  const showText = editing ? query : (selected ? selected.name_en : '')

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        value={showText}
        disabled={disabled}
        placeholder={readOnlyLabel ?? placeholder ?? 'Account'}
        autoFocus={autoFocus}
        onFocus={() => { setEditing(true); setOpen(true) }}
        onBlur={() => { setTimeout(() => { setEditing(false); setOpen(false) }, 120) }}
        onChange={(e) => { setQuery(e.target.value); setEditing(true); setOpen(true) }}
        onKeyDown={onKeyDown}
        style={{
          width: '100%',
          height: tokens.rowHeight - 8,
          padding: '0 8px',
          border: `1px solid transparent`,
          borderRadius: 4,
          background: 'transparent',
          font: 'inherit',
          color: disabled ? c.inkMuted : c.ink,
          outline: 'none',
        }}
        onMouseDown={() => inputRef.current?.select()}
      />

      <Popper open={open && results.length > 0} anchorEl={inputRef.current}
        placement="bottom-start" style={{ zIndex: 1400 }}>
        <Paper variant="outlined" sx={{
          minWidth: 360, maxWidth: 'calc(100vw - 24px)', mt: 0.75, p: 0.75,
          borderColor: c.borderStrong, borderRadius: '8px',
          boxShadow: 'var(--shadow-popover)',
        }}>
          <Box role="listbox" aria-label={t('entry.matchingAccounts')}
            sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {results.map((l, i) => {
              const active = i === highlight
              const bal = balanceOf?.(l.code)
              const shown = bal !== undefined ? withSide(bal) : null
              const name = (ar && l.name_ar) || l.name_en
              return (
                <Box key={l.id} role="option" aria-selected={active}
                  onMouseDown={(e) => { e.preventDefault(); choose(l) }}
                  onMouseEnter={() => setHighlight(i)}
                  sx={{
                    px: 1.25, py: 0.9, cursor: 'pointer', borderRadius: '6px',
                    bgcolor: active ? c.primarySoft : 'transparent',
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography dir="auto" sx={{
                      fontSize: 14, lineHeight: '20px',
                      fontWeight: active ? 600 : 500,
                      color: active ? c.primaryInk : c.ink,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{name}</Typography>
                    <Typography component="div" sx={{ fontSize: 12, color: c.inkMuted }}>
                      <span className="doc-no" style={{ fontWeight: 400 }}>{l.code}</span>
                      {l.maintain_bill_wise && (
                        <Box component="span" sx={{ color: c.warningInk }}>
                          {' · '}{t('entry.billWise')}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  {shown && (
                    <Typography className="num" sx={{
                      fontSize: 12.5, lineHeight: '20px', color: c.inkSecondary,
                      whiteSpace: 'nowrap', fontWeight: 500,
                    }}>
                      {shown.amount}{shown.side && ` ${shown.side}`}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 1.75, px: 1.25, pt: 0.75, pb: 0.25,
            mt: 0.5, borderTop: `1px solid ${c.divider}`, fontSize: 11, color: c.inkMuted,
          }}>
            <span><span className="doc-no">↑↓</span> {t('shell.move')}</span>
            <span><span className="doc-no">Enter</span> {t('entry.select')}</span>
            {onCreateRequest && (
              <span><span className="doc-no">Alt+C</span> {t('entry.createParty')}</span>
            )}
          </Box>
        </Paper>
      </Popper>
    </Box>
  )
})

export default LedgerPicker
